import {useEffect, useMemo, useState} from "react";
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Stack,
    Typography,
} from "@mui/material";
import {useTranslation} from "react-i18next";
import {ProlongEvent} from "crqlar-react-common/dist/models/floorplan/floorplanEvents.dto";
import {Reservation, ReservationStatus} from "crqlar-react-common/dist/models/reservation/reservation.model";
import {Profile} from "crqlar-react-common/dist/models/profile/profile.model";
import {Resource} from "crqlar-react-common/dist/models/resource/resource.model";
import {FloorplanService} from "crqlar-react-common/dist/services/floorplan/floorplan.service";
import {ReservationService} from "crqlar-react-common/dist/services/reservation/reservation.service";
import {convertLocalDateToDate, endOfDay, formatFullDMY, startOfDay} from "crqlar-react-common/dist/utils/date.util";
import {FloorplanReservationItemDTO} from "crqlar-react-common/models/floorplan/floorplanReservationItem.dto";
import {useAllChildResources} from "crqlar-react-common/dist/utils/hooks/resources/useAllRootResources";
import {useResources} from "crqlar-react-common/dist/utils/hooks/resources/useResources";
import {useReadOnly} from "crqlar-react-common/dist/utils/hooks/users/useReadOnly";
import {getOverlappingReservations} from "../floorPlan/v2/utils/drop-utils";
import CloseIcon from "@mui/icons-material/Close";
import {useSnackbar} from "notistack";
import {useQueryClient} from "react-query";
import PreviewProfileInformation from "./PreviewProfileInformation";
import CurrentStayInformation from "./CurrentStayInformation";
import EventsUpdateDialogTables, {
    AdjustReservationDisplayRow,
    ExistingReservationDisplayRow,
} from "./EventsUpdateDialogTables";

interface Props {
    open: boolean;
    event?: ProlongEvent;
    guestLabel?: string;
    accommodationStayLabel?: string;
    restaurantResourceId?: string;
    onOpenReservation?: (reservation: Reservation) => void;
    onOpenAccommodationReservation?: (reservation: Reservation, profile?: Profile) => void;
    onBodyReady?: () => void;
    onClose: () => void;
}

type RestaurantReservationOverviewRow =
    | {
        key: string;
        kind: "existing";
        date: string;
        period: string;
        time: string;
        tableResourceIds: string[];
        reservationStarted: boolean;
        reservationId?: string;
        reservation?: Reservation;
        startDate?: Date;
        endDate?: Date;
        assignedDay?: string;
        startDay?: string | null;
        endDay?: string | null;
        startTime?: string | null;
        endTime?: string | null;
        eatPeriod?: string | null;
    }
    | {
        key: string;
        kind: "future";
        date: string;
        period: string;
        time: string;
        tableResourceIds: string[];
        serviceId?: string | null;
        startDate?: Date;
        endDate?: Date;
        assignedDay?: string;
        startDay?: string | null;
        endDay?: string | null;
        startTime?: string | null;
        endTime?: string | null;
        eatPeriod?: string | null;
    };

interface ExistingReservationUpdateSnapshot {
    before: Reservation;
    after: Reservation;
}

interface ExistingReservationUpdateRequest {
    before: Reservation;
    nextResourceIds: string[];
}

const cloneReservationSnapshot = (reservation: Reservation): Reservation => structuredClone(reservation);

const haveSameResourceIds = (left?: string[] | null, right?: string[] | null) => {
    const leftIds = [...(left ?? [])].sort();
    const rightIds = [...(right ?? [])].sort();
    return leftIds.length === rightIds.length
        && leftIds.every((resourceId, index) => resourceId === rightIds[index]);
};

const undoCreatedReservation = async (reservation: Reservation) => {
    if (!reservation.id) return;

    try {
        await ReservationService.updateReservation({
            ...cloneReservationSnapshot(reservation),
            status: ReservationStatus.CANCELLED,
        }, true);
    } catch (error) {
        console.error(error);
    }

    await ReservationService.deleteReservation({id: [reservation.id]});
};

const getLocalDateTime = (day?: string | null, time?: string | null, useDayEnd = false) => {
    const date = convertLocalDateToDate(day ?? undefined);
    if (!date) return undefined;
    if (!time) return useDayEnd ? endOfDay(date) : date;

    const [hours, minutes, seconds] = time.split(":").map(value => Number(value));
    date.setHours(hours || 0, minutes || 0, seconds || 0, 0);
    return date;
};

const getRestaurantReservationStarted = (
    reservation?: Reservation,
    meta?: ProlongEvent["restaurantReservationsMeta"][number],
) => {
    const startDay = meta?.startDay ?? reservation?.actualStartDay ?? reservation?.expectedStartDay;
    const startTime = meta?.startTime ?? reservation?.actualStartTime ?? reservation?.expectedStartTime;
    const startDate = getLocalDateTime(startDay, startTime);
    return !!startDate && startDate.getTime() <= Date.now();
};

const formatDayLabel = (day?: string | null) => {
    const date = convertLocalDateToDate(day ?? undefined);
    return date ? formatFullDMY(date) : day ?? "";
};

const formatTimeLabel = (time?: string | null) => time ? time.slice(0, 5) : "";

const EventsUpdateDialog = ({
                                open,
                                event,
                                guestLabel,
                                accommodationStayLabel,
                                restaurantResourceId,
                                onOpenReservation,
                                onOpenAccommodationReservation,
                                onBodyReady,
                                onClose,
                            }: Props) => {
    const {t} = useTranslation();
    const queryClient = useQueryClient();
    const {enqueueSnackbar, closeSnackbar} = useSnackbar();
    // Read-only users can review the assignment table, but not apply updates.
    const readOnly = useReadOnly();
    const [rowTableSelections, setRowTableSelections] = useState<Record<string, Resource[]>>({});
    const [assignedReservationsByDay, setAssignedReservationsByDay] = useState<Record<string, FloorplanReservationItemDTO[]>>({});
    const [assignedReservationsLoading, setAssignedReservationsLoading] = useState(false);
    const [assignedReservationsError, setAssignedReservationsError] = useState<string | null>(null);
    const [confirmUpdateOpen, setConfirmUpdateOpen] = useState(false);
    const [updateSaving, setUpdateSaving] = useState(false);
    // Long stays (e.g. 6 weeks) make the body's conflict-checking render
    // expensive enough to block the dialog from appearing. Render the shell
    // first with a spinner, then mount the body on the next frame so the user
    // sees an immediate response to their click.
    const [bodyReady, setBodyReady] = useState(false);

    const restaurantResourceIdSet = useMemo(
        () => new Set(restaurantResourceId ? [restaurantResourceId] : []),
        [restaurantResourceId],
    );
    const activeRestaurantResourceIds = useMemo(
        () => open && restaurantResourceId ? [restaurantResourceId] : [],
        [open, restaurantResourceId],
    );
    const restaurantTableResourceIds = useMemo(() => {
        if (!open || !event) return [];

        return Array.from(new Set(
            [
                ...(event.restaurantReservationsMeta ?? []).flatMap(meta => meta.resourceIds ?? []),
                ...(event.missingRestaurantHints ?? []).flatMap(hint => hint.resourceIds ?? []),
                ...(event._restaurantReservations ?? []).flatMap(reservation => reservation.resourceIds ?? []),
            ].filter((resourceId): resourceId is string => !!resourceId && !restaurantResourceIdSet.has(resourceId))
        ));
    }, [event, open, restaurantResourceIdSet]);
    const childRestaurantTableResources = useAllChildResources(activeRestaurantResourceIds);
    const restaurantTableResources = useResources(restaurantTableResourceIds);
    const restaurantTableOptions = useMemo(() => {
        const resourceById = new Map<string, Resource>();
        childRestaurantTableResources.forEach(resource => {
            if (resource.id) resourceById.set(resource.id, resource);
        });
        restaurantTableResources.forEach(resource => {
            if (resource.id) resourceById.set(resource.id, resource);
        });
        return Array.from(resourceById.values()).sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    }, [childRestaurantTableResources, restaurantTableResources]);
    const restaurantTableResourceById = useMemo(
        () => new Map(restaurantTableOptions.map(resource => [resource.id, resource])),
        [restaurantTableOptions],
    );

    const assignedReservationDays = useMemo(() => {
        if (!open || !event || !restaurantResourceId) return [];

        const restaurantReservations = event._restaurantReservations ?? [];
        const restaurantMetas = event.restaurantReservationsMeta ?? [];
        const rowCount = Math.max(restaurantReservations.length, restaurantMetas.length);
        const editableExistingDays = Array.from({length: rowCount}).map((_, index) => {
            const reservation = restaurantReservations[index];
            const meta = restaurantMetas[index];
            if (getRestaurantReservationStarted(reservation, meta)) return undefined;
            return meta?.startDay ?? reservation?.expectedStartDay ?? reservation?.actualStartDay;
        });
        const futureDays = (event.missingRestaurantHints ?? []).map(hint => hint.date);

        return Array.from(new Set([
            ...editableExistingDays,
            ...futureDays,
        ].filter((day): day is string => !!day))).sort();
    }, [event, open, restaurantResourceId]);

    useEffect(() => {
        if (!open || !restaurantResourceId || !assignedReservationDays.length) {
            setAssignedReservationsByDay({});
            setAssignedReservationsLoading(false);
            setAssignedReservationsError(null);
            return;
        }

        let cancelled = false;
        const loadAssignedReservations = async () => {
            setAssignedReservationsLoading(true);
            setAssignedReservationsError(null);

            try {
                const fetchStatuses = [
                    ReservationStatus.CONFIRMED,
                    ReservationStatus.ACTIVE,
                    ReservationStatus.COMPLETED,
                    ReservationStatus.PENDING,
                ];
                const entries = await Promise.all(assignedReservationDays.map(async (day) => {
                    const dayDate = new Date(day);
                    const assignedReservations = await FloorplanService.getAssignedReservations({
                        restaurantId: restaurantResourceId,
                        limit: {offset: 0, limit: 1000},
                        timespan: {start: startOfDay(dayDate), end: endOfDay(dayDate)},
                        status: fetchStatuses,
                    });
                    return [day, assignedReservations] as const;
                }));

                if (!cancelled) setAssignedReservationsByDay(Object.fromEntries(entries));
            } catch (error) {
                console.error(error);
                if (!cancelled) {
                    setAssignedReservationsByDay({});
                    setAssignedReservationsError(t("Failed to load conflict data"));
                }
            } finally {
                if (!cancelled) setAssignedReservationsLoading(false);
            }
        };

        void loadAssignedReservations();
        return () => {
            cancelled = true;
        };
    }, [assignedReservationDays, open, restaurantResourceId, t]);

    useEffect(() => {
        if (!open) {
            setConfirmUpdateOpen(false);
            setUpdateSaving(false);
            setRowTableSelections({});
        }
    }, [open]);

    useEffect(() => {
        if (!open) {
            setBodyReady(false);
            return;
        }
        const timer = setTimeout(() => {
            setBodyReady(true);
            onBodyReady?.();
        }, 0);
        return () => clearTimeout(timer);
    }, [onBodyReady, open]);

    if (!event) return null;

    if (!bodyReady) {
        // Preview cards are cheap to render and show the operator who/what they
        // just clicked on. Only the tables area is expensive enough to need a
        // spinner. JSX structure matches the full render so React reconciles
        // the cards instead of remounting them when bodyReady flips.
        return (
            <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
                <DialogTitle sx={{fontWeight: 700}}>
                    {t("Update reservations")}
                </DialogTitle>
                <DialogContent sx={{display: "flex", flexDirection: "column", overflow: "hidden", p: 0}}>
                    <Stack gap={2} sx={{px: 3, pt: 2, pb: 2}}>
                        <PreviewProfileInformation
                            event={event}
                            guestLabel={guestLabel}
                            accommodationStayLabel={accommodationStayLabel}
                            onOpenAccommodationReservation={onOpenAccommodationReservation}
                        />
                        <CurrentStayInformation event={event} onOpenReservation={onOpenReservation}/>
                    </Stack>
                    <Box sx={{
                        minHeight: 200,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 1.5,
                    }}>
                        <CircularProgress/>
                        <Typography variant="body2" color="text.secondary">
                            {t("Loading...")}
                        </Typography>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose}>{t("Close")}</Button>
                </DialogActions>
            </Dialog>
        );
    }

    const formatDateRange = (startDay?: string | null, endDay?: string | null) => {
        const start = formatDayLabel(startDay);
        const end = formatDayLabel(endDay);
        return start && end && start !== end ? `${start} - ${end}` : start || end || "";
    };

    const formatTimeRange = (startTime?: string | null, endTime?: string | null) => {
        const start = formatTimeLabel(startTime);
        const end = formatTimeLabel(endTime);
        return start && end ? `${start} - ${end}` : start || end || "";
    };

    const formatEatPeriod = (eatPeriod?: string | null) => {
        if (!eatPeriod) return "";
        switch (eatPeriod) {
            case "breakfast": return t("Breakfast");
            case "lunch":     return t("Lunch");
            case "dinner":    return t("Dinner");
            default:          return eatPeriod;
        }
    };

    // Strip the restaurant id itself out of resourceIds so we only keep table-level ids.
    const getTableResourceIds = (resourceIds?: string[] | null) =>
        resourceIds?.filter((resourceId): resourceId is string => !!resourceId && !restaurantResourceIdSet.has(resourceId)) ?? [];

    const getTableLabelForIds = (resourceIds: string[]) => {
        const labels = resourceIds
            .map(id => restaurantTableResourceById.get(id)?.name)
            .filter((name): name is string => !!name);
        return labels.join(", ");
    };

    const primaryReservation = event._accommodationReservations[0];

    // Build the unified row list (existing reservations + suggested-future hints).
    const restaurantReservationRows: RestaurantReservationOverviewRow[] = (() => {
        const restaurantReservations = event._restaurantReservations ?? [];
        const restaurantMetas = event.restaurantReservationsMeta ?? [];
        const rowCount = Math.max(restaurantReservations.length, restaurantMetas.length);

        const existingRows = Array.from({length: rowCount}).map((_, index): RestaurantReservationOverviewRow => {
            const reservation = restaurantReservations[index];
            const meta = restaurantMetas[index];
            const tableResourceIds = getTableResourceIds(meta?.resourceIds ?? reservation?.resourceIds);
            const startDay = meta?.startDay ?? reservation?.expectedStartDay ?? reservation?.actualStartDay;
            const endDay = meta?.endDay ?? reservation?.expectedEndDay ?? reservation?.actualEndDay;
            const startTime = meta?.startTime ?? reservation?.expectedStartTime ?? reservation?.actualStartTime;
            const endTime = meta?.endTime ?? reservation?.expectedEndTime ?? reservation?.actualEndTime;
            const startDate = getLocalDateTime(startDay, startTime)
                ?? reservation?.expectedStartDate
                ?? reservation?.actualStartDate;
            const endDate = getLocalDateTime(endDay, endTime, true)
                ?? reservation?.expectedEndDate
                ?? reservation?.actualEndDate;

            return {
                kind: "existing",
                key: `restaurant-reservation-${reservation?.id ?? `${startDay}-${startTime}-${index}`}`,
                date: formatDateRange(startDay, endDay),
                period: formatEatPeriod(meta?.eatPeriod),
                time: formatTimeRange(startTime, endTime),
                tableResourceIds,
                reservationStarted: getRestaurantReservationStarted(reservation, meta),
                reservationId: reservation?.id,
                reservation,
                startDate,
                endDate,
                assignedDay: startDay,
                startDay,
                endDay,
                startTime,
                endTime,
                eatPeriod: meta?.eatPeriod,
            };
        });

        const futureRows = (event.missingRestaurantHints ?? []).map((hint, index): RestaurantReservationOverviewRow => ({
            kind: "future",
            key: `future-restaurant-reservation-${hint.date}-${hint.eatPeriod}-${index}`,
            date: formatDayLabel(hint.date),
            period: formatEatPeriod(hint.eatPeriod),
            time: formatTimeRange(hint.startTime, hint.endTime),
            tableResourceIds: getTableResourceIds(hint.resourceIds),
            serviceId: hint.serviceId,
            startDate: getLocalDateTime(hint.date, hint.startTime),
            endDate: getLocalDateTime(hint.date, hint.endTime, true),
            assignedDay: hint.date,
            startDay: hint.date,
            endDay: hint.date,
            startTime: hint.startTime,
            endTime: hint.endTime,
            eatPeriod: hint.eatPeriod,
        }));

        return [...existingRows, ...futureRows];
    })();

    // Started existing reservations are read-only; everything else is adjustable.
    const shouldValidate = (row: RestaurantReservationOverviewRow) =>
        row.kind !== "existing" || !row.reservationStarted;

    const getCurrentTables = (row: RestaurantReservationOverviewRow) =>
        row.tableResourceIds.map(id => restaurantTableResourceById.get(id) || ({id, name: id} as Resource));

    const getSelectedTables = (row: RestaurantReservationOverviewRow) =>
        rowTableSelections[row.key] ?? getCurrentTables(row);

    const getInitialTableIds = (row: RestaurantReservationOverviewRow) =>
        getCurrentTables(row).map(r => r.id).filter((id): id is string => !!id).sort();

    const hasSelectionChanged = (row: RestaurantReservationOverviewRow) => {
        if (!Object.prototype.hasOwnProperty.call(rowTableSelections, row.key)) return false;
        const selected = rowTableSelections[row.key].map(r => r.id).filter((id): id is string => !!id).sort();
        const initial = getInitialTableIds(row);
        return selected.length !== initial.length
            || selected.some((id, i) => id !== initial[i]);
    };

    const getRowAssigned = (row: RestaurantReservationOverviewRow) =>
        row.assignedDay ? assignedReservationsByDay[row.assignedDay] ?? [] : [];

    const getOptionConflicts = (row: RestaurantReservationOverviewRow, optionId?: string) => {
        if (!shouldValidate(row) || !optionId || !row.startDate || !row.endDate) return [];
        return getOverlappingReservations(
            optionId,
            getRowAssigned(row),
            row.startDate,
            row.endDate,
            row.kind === "existing" ? row.reservationId : undefined,
        );
    };

    // Build display-row data for the new table component
    const existingDisplayRows: ExistingReservationDisplayRow[] = restaurantReservationRows
        .filter((row): row is Extract<RestaurantReservationOverviewRow, {kind: "existing"}> => row.kind === "existing")
        .map(row => ({
            key: row.key,
            dateLabel: row.date,
            timeLabel: row.time,
            periodLabel: row.period,
            tableLabel: getTableLabelForIds(row.tableResourceIds),
        }));

    const adjustRows = restaurantReservationRows.filter(shouldValidate);
    const adjustDisplayRows: AdjustReservationDisplayRow[] = adjustRows.map(row => {
        const selectedTables = getSelectedTables(row);
        const selectedConflicts = selectedTables.flatMap(t => getOptionConflicts(row, t.id));
        const optionConflicts = new Map<string, FloorplanReservationItemDTO[]>();
        // Sort conflicting options to the bottom; numeric-aware otherwise.
        const sortedOptions = restaurantTableOptions.slice().sort((a, b) => {
            const aConflict = getOptionConflicts(row, a.id).length > 0;
            const bConflict = getOptionConflicts(row, b.id).length > 0;
            if (aConflict !== bConflict) return aConflict ? 1 : -1;
            return (a.name || a.id || "").localeCompare(b.name || b.id || "", undefined, {numeric: true});
        });
        sortedOptions.forEach(option => {
            if (option.id) optionConflicts.set(option.id, getOptionConflicts(row, option.id));
        });

        return {
            key: row.key,
            dateLabel: row.date,
            timeLabel: row.time,
            periodLabel: row.period,
            suggestedMissing: row.kind === "future",
            selectedTables,
            options: sortedOptions,
            optionConflicts,
            selectedConflicts,
            selectDisabled: row.kind === "existing" && row.reservationStarted,
            showStatus: shouldValidate(row) && selectedTables.some(table => !!table.id),
        };
    });

    // Save / undo plumbing
    const existingRowsForUpdate = restaurantReservationRows.filter((
        row,
    ): row is Extract<RestaurantReservationOverviewRow, {kind: "existing"}> & {reservation: Reservation} =>
        row.kind === "existing" && !!row.reservation && shouldValidate(row) && hasSelectionChanged(row));
    const futureRowsForCreate = restaurantReservationRows.filter((
        row,
    ): row is Extract<RestaurantReservationOverviewRow, {kind: "future"}> => row.kind === "future");

    const getSelectedTableIds = (row: RestaurantReservationOverviewRow) =>
        Array.from(new Set(
            getSelectedTables(row).map(r => r.id).filter((id): id is string => !!id)
        ));

    const buildResourceIds = (tableResourceIds: string[]) =>
        Array.from(new Set([restaurantResourceId, ...tableResourceIds].filter((id): id is string => !!id)));

    // Confirm dialog summary rows
    // Either an existing reservation getting its tables updated, or a brand-new one being created.
    const confirmSummaryRows: Array<{
        kind: "update" | "create";
        key: string;
        dateLabel: string;
        timeLabel: string;
        periodLabel: string;
        previousTablesLabel?: string;
        nextTablesLabel: string;
    }> = [
        ...existingRowsForUpdate.map(row => ({
            kind: "update" as const,
            key: row.key,
            dateLabel: row.date,
            timeLabel: row.time,
            periodLabel: row.period,
            previousTablesLabel: getTableLabelForIds(row.tableResourceIds) || "-",
            nextTablesLabel: getTableLabelForIds(getSelectedTableIds(row)) || "-",
        })),
        ...futureRowsForCreate.map(row => ({
            kind: "create" as const,
            key: row.key,
            dateLabel: row.date,
            timeLabel: row.time,
            periodLabel: row.period,
            nextTablesLabel: getTableLabelForIds(getSelectedTableIds(row)) || "-",
        })),
    ];

    const getSectorId = () => event._restaurantReservations?.find(r => !!r.sectorId)?.sectorId;
    const getServiceId = () => event._restaurantReservations?.find(r => !!r.serviceId)?.serviceId;

    const invalidateReservationQueries = async () => {
        await Promise.all([
            queryClient.invalidateQueries("floorplanEvents"),
            queryClient.invalidateQueries("floorplanAssigned"),
            queryClient.invalidateQueries("floorplanUnassigned"),
            queryClient.invalidateQueries("floorplanHalfboardProfiles"),
        ]);
    };

    const buildFutureReservation = (row: Extract<RestaurantReservationOverviewRow, {kind: "future"}>): Reservation => ({
        profileIds: [event.profileId],
        primaryProfileId: event.profileId,
        serviceId: row.serviceId ?? getServiceId(),
        sectorId: getSectorId(),
        resourceIds: buildResourceIds(getSelectedTableIds(row)),
        guestCounts: primaryReservation?.guestCounts,
        expectedStartDate: row.startDate,
        expectedStartDay: row.startDay ?? undefined,
        expectedStartTime: row.startTime ?? undefined,
        expectedEndDate: row.endDate,
        expectedEndDay: row.endDay ?? row.startDay ?? undefined,
        expectedEndTime: row.endTime ?? undefined,
        status: ReservationStatus.CONFIRMED,
        meta: row.eatPeriod ? {eatPeriod: row.eatPeriod} : undefined,
    });

    const showUpdateSuccessSnackbar = (
        snapshots: ExistingReservationUpdateSnapshot[],
        created: Reservation[],
    ) => {
        closeSnackbar();
        enqueueSnackbar(t("Reservations updated"), {
            variant: "success",
            autoHideDuration: 5000,
            action: (snackbarId) => (
                <>
                    <Button
                        color="inherit"
                        size="small"
                        onClick={async () => {
                            closeSnackbar(snackbarId);
                            const rollbacks = snapshots
                                .filter(s => !haveSameResourceIds(s.before.resourceIds, s.after.resourceIds))
                                .map(s => ReservationService.updateReservation(cloneReservationSnapshot(s.before), true));
                            const deletions = created.filter(r => !!r.id).map(undoCreatedReservation);
                            const results = await Promise.allSettled([...rollbacks, ...deletions]);
                            const failed = results.filter(r => r.status === "rejected");
                            await invalidateReservationQueries();

                            if (failed.length > 0) {
                                failed.forEach(r => r.status === "rejected" && console.error(r.reason));
                                enqueueSnackbar(t("Failed to undo reservation update"), {variant: "error", autoHideDuration: 5000});
                            }
                        }}
                    >
                        {t("Undo")}
                    </Button>
                    <IconButton size="small" color="inherit" onClick={() => closeSnackbar(snackbarId)}>
                        <CloseIcon fontSize="small"/>
                    </IconButton>
                </>
            ),
        });
    };

    const handleConfirmUpdate = async () => {
        setUpdateSaving(true);

        const updates: ExistingReservationUpdateRequest[] = existingRowsForUpdate.map(row => ({
            before: cloneReservationSnapshot(row.reservation),
            nextResourceIds: buildResourceIds(getSelectedTableIds(row)),
        }));
        const snapshots: ExistingReservationUpdateSnapshot[] = [];
        const created: Reservation[] = [];

        try {
            for (const update of updates) {
                const after = await ReservationService.updateReservation({
                    ...cloneReservationSnapshot(update.before),
                    resourceIds: update.nextResourceIds,
                }, true);
                snapshots.push({before: update.before, after: cloneReservationSnapshot(after)});
            }

            for (const row of futureRowsForCreate) {
                const createdReservation = await ReservationService.createReservation(buildFutureReservation(row), true);
                created.push(createdReservation);
            }

            setConfirmUpdateOpen(false);
            setUpdateSaving(false);
            setRowTableSelections({});
            void invalidateReservationQueries();
            onClose();
            showUpdateSuccessSnackbar(snapshots, created);
        } catch (error) {
            console.error(error);
            setUpdateSaving(false);
            enqueueSnackbar(t("Failed to update reservations"), {
                variant: "error",
                autoHideDuration: 5000,
            });
        }
    };

    const handleClose = () => {
        setConfirmUpdateOpen(false);
        onClose();
    };

    return (
        <>
            <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
                <DialogTitle sx={{fontWeight: 700}}>
                    {t("Update reservations")}
                </DialogTitle>
                <DialogContent sx={{display: "flex", flexDirection: "column", overflow: "hidden", p: 0}}>
                    {/* Sticky preview cards. */}
                    <Stack gap={2} sx={{px: 3, pt: 2, pb: 2}}>
                        <PreviewProfileInformation
                            event={event}
                            guestLabel={guestLabel}
                            accommodationStayLabel={accommodationStayLabel}
                            onOpenAccommodationReservation={onOpenAccommodationReservation}
                        />
                        <CurrentStayInformation event={event} onOpenReservation={onOpenReservation}/>
                    </Stack>

                    {/* Tables area: only this scrolls. */}
                    <Box sx={{minHeight: 0, overflowY: "auto", px: 3, pb: 2}}>
                        <EventsUpdateDialogTables
                            existingRows={existingDisplayRows}
                            adjustRows={adjustDisplayRows}
                            onSelectionChange={(rowKey, value) =>
                                setRowTableSelections(current => ({...current, [rowKey]: value}))
                            }
                            isLoadingAvailability={assignedReservationsLoading}
                            availabilityError={assignedReservationsError}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>
                        {t("Close")}
                    </Button>
                    <Button
                        variant="contained"
                        onClick={() => setConfirmUpdateOpen(true)}
                        disabled={assignedReservationsLoading || readOnly}
                        startIcon={assignedReservationsLoading ? <CircularProgress color="inherit" size={16}/> : undefined}
                    >
                        {assignedReservationsLoading ? t("Loading...") : t("Update")}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={confirmUpdateOpen} onClose={() => setConfirmUpdateOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{fontWeight: 700}}>
                    {t("Confirm update")}
                </DialogTitle>
                <DialogContent>
                    <Typography color="text.secondary" sx={{pb: 1.5}}>
                        {t("These changes will be applied:")}
                    </Typography>
                    {confirmSummaryRows.length === 0 ? (
                        <Typography color="text.secondary">
                            {t("No changes")}
                        </Typography>
                    ) : (
                        <Box sx={{
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: 0.75,
                            p: 1.25,
                            bgcolor: "background.paper",
                        }}>
                            {/* Column headers (desktop only) — same shape as the rows below. */}
                            <Box sx={{
                                display: {xs: "none", sm: "grid"},
                                gridTemplateColumns: "110px 110px minmax(0, 1fr)",
                                columnGap: 1.5,
                                pb: 0.75,
                                borderBottom: "1px solid",
                                borderColor: "divider",
                            }}>
                                <Typography variant="caption" sx={{fontWeight: 700, textTransform: "uppercase", color: "text.secondary", letterSpacing: 0.5}}>
                                    {t("Date")}
                                </Typography>
                                <Typography variant="caption" sx={{fontWeight: 700, textTransform: "uppercase", color: "text.secondary", letterSpacing: 0.5}}>
                                    {t("Period")}
                                </Typography>
                                <Typography variant="caption" sx={{fontWeight: 700, textTransform: "uppercase", color: "text.secondary", letterSpacing: 0.5}}>
                                    {t("Table")}
                                </Typography>
                            </Box>

                            {confirmSummaryRows.map((row, index) => (
                                <Box
                                    key={row.key}
                                    sx={{
                                        display: "grid",
                                        gridTemplateColumns: {xs: "1fr", sm: "110px 110px minmax(0, 1fr)"},
                                        columnGap: {xs: 0, sm: 1.5},
                                        rowGap: {xs: 0.25, sm: 0},
                                        alignItems: "center",
                                        py: 0.85,
                                        borderTop: index === 0 ? 0 : "1px solid",
                                        borderColor: "divider",
                                    }}
                                >
                                    <Box>
                                        <Typography sx={{fontWeight: 700, lineHeight: 1.3}}>
                                            {row.dateLabel || "-"}
                                        </Typography>
                                        {row.timeLabel && (
                                            <Typography color="text.secondary" sx={{lineHeight: 1.3}}>
                                                {row.timeLabel}
                                            </Typography>
                                        )}
                                    </Box>
                                    <Typography color="text.secondary" sx={{lineHeight: 1.3, alignSelf: "start"}}>
                                        {row.periodLabel || "-"}
                                    </Typography>
                                    {row.kind === "update" ? (
                                        <Box sx={{display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap"}}>
                                            <Typography color="text.secondary" sx={{lineHeight: 1.3, textDecoration: "line-through"}}>
                                                {row.previousTablesLabel}
                                            </Typography>
                                            <Typography color="text.secondary" sx={{lineHeight: 1.3}}>→</Typography>
                                            <Typography sx={{lineHeight: 1.3, fontWeight: 700}}>
                                                {row.nextTablesLabel}
                                            </Typography>
                                        </Box>
                                    ) : (
                                        <Box sx={{display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap"}}>
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    fontWeight: 700,
                                                    textTransform: "uppercase",
                                                    letterSpacing: 0.5,
                                                    px: 0.6,
                                                    py: 0.1,
                                                    borderRadius: 0.5,
                                                    bgcolor: "success.50",
                                                    color: "success.dark",
                                                }}
                                            >
                                                {t("New")}
                                            </Typography>
                                            <Typography sx={{lineHeight: 1.3, fontWeight: 700}}>
                                                {row.nextTablesLabel}
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                            ))}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmUpdateOpen(false)} disabled={updateSaving}>
                        {t("Cancel")}
                    </Button>
                    <Button variant="contained" onClick={handleConfirmUpdate} disabled={updateSaving || readOnly}>
                        {updateSaving
                            ? t("Updating...")
                            : t("Update")}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default EventsUpdateDialog;
